import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl, getLocale } from 'umi';
import moment from 'moment';

import {
  formatNum,
  formatPercentNum,
  getIsDollarText,
  formatExeclNormalPercent,
  handleDownload,
  formatExeclNum,
  formatExeclDecimal,
  getIsErrorValue,
  formatPercentNumNoPercent,
  formatSmallValue
} from '@/pages/dashboard/utils/utils';
import useOverviewLocalesIntl from '@/pages/dashboard/pages/overview/_config/useOverviewLocalesIntl';
import { EngagementRevenueMetricsItemProps } from '@/pages/dashboard/pages/overview/_config/interface';
import { useSelectorMap, useWeekDay } from '@/pages/dashboard/utils/customHook';
import { TABLE_CELL_PADDING_LEFT, TABLE_CELL_PADDING_RIGHT } from '@/pages/home/_config/config';
import { hasPercentList } from './_config';
import { cloneDeep } from 'lodash-es';

const DATE_COLUMN_WIDTH = 120;
const METRIC_COLUMN_WIDTH = 90;


const useIndex = (props: EngagementRevenueMetricsItemProps) => {
  const {
    defaultTab,
    selected,
    defaultTabText,
    loading: loadingProps,
    defaultData,
    excelFilterName,
    tabList,
    update,
    type = 'DAY'
  } = props;
  const { getWeekDayStr, getWeekDay } = useWeekDay();
  const { getSelectorText } = useSelectorMap();
  const { formatMessage } = useIntl(); // 中英切换方法

  const {
    CHART_TYPE,
    ENGAGEMENT_METRICS_TRENDS,
    REVENUE_METRICS_TRENDS,
    ENGAGEMENT_METRICS_TRENDS_TAB_LIST,
    LINE_METRICS_LEGEND_MAP,
    METRICS_CONFIG,
    WEIGHTED_METRICS_CONFIG,
    DATE_DIMENSION,
  } = useOverviewLocalesIntl();
  const { DAY, MONTH } = DATE_DIMENSION;

  const userQuota = ['dau', 'wau', 'mau', 'new', 'tru', 'pcu', 'acu', 'payer', 'avg.wau', 'avg.mau'];
  const [data, setData] = useState({
    [CHART_TYPE.COLUMN]: {
      series: [],
      xAxis: [],
      legend: [],
    },
    [CHART_TYPE.LINE]: [],
    [CHART_TYPE.TABLE]: [],
  } as {
    [key: string]: any;
  });
  const [loading, setLoading] = useState(false);
  const [chartType, setChartType] = useState(CHART_TYPE.LINE);
  const [dataType, setDataType] = useState(defaultTab);
  const [selectedValue, setSelectedValue] = useState(selected);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [dataTypeText, setDataTypeText] = useState(defaultTabText);
  const isDay = type === DAY;
  const isPercent = hasPercentList?.includes(dataType);
  const isUser = userQuota.includes(dataType);
  // d2/3/7, w2/3, m2/3
  const { retention2, retention3, retention7 } = useMemo(() => {
    if (type === DATE_DIMENSION.DAY) {
      return {
        retention2: formatMessage({ id: `overview_${type?.toLowerCase()}_retention_2` }),
        retention3: formatMessage({ id: `overview_${type?.toLowerCase()}_retention_3` }),
        retention7: formatMessage({ id: `overview_${type?.toLowerCase()}_retention_7` }),
      };
    }
    return {
      retention2: formatMessage({ id: `overview_${type?.toLowerCase()}_retention_2` }),
      retention3: formatMessage({ id: `overview_${type?.toLowerCase()}_retention_3` }),
    };
  }, [type, getLocale()]);

  const { wRetention2, wRetention3, wRetention7 } = useMemo(() => {
    if (type === DATE_DIMENSION.DAY) {
      return {
        wRetention2: formatMessage({ id: `overview_${type?.toLowerCase()}_Weighted_2` }),
        wRetention3: formatMessage({ id: `overview_${type?.toLowerCase()}_Weighted_3` }),
        wRetention7: formatMessage({ id: `overview_${type?.toLowerCase()}_Weighted_7` }),
      };
    }
    return {
      wRetention2: formatMessage({ id: `overview_${type?.toLowerCase()}_Weighted_2` }),
      wRetention3: formatMessage({ id: `overview_${type?.toLowerCase()}_Weighted_3` }),
    };
  }, [type, getLocale()]);

  // d_2/3/7, w_2/3, m_2/3
  const { retentionValue2, retentionValue3, retentionValue7 } = useMemo(() => {
    if (type === DATE_DIMENSION.DAY) {
      return {
        retentionValue2: `${METRICS_CONFIG[type]}_2`,
        retentionValue3: `${METRICS_CONFIG[type]}_3`,
        retentionValue7: `${METRICS_CONFIG[type]}_7`,
      };
    }
    return {
      retentionValue2: `${METRICS_CONFIG[type]}_2`,
      retentionValue3: `${METRICS_CONFIG[type]}_3`,
    };
  }, [type]);

  const { wRetentionValue2, wRetentionValue3, wRetentionValue7 } = useMemo(() => {
    if (type === DATE_DIMENSION.DAY) {
      return {
        wRetentionValue2: `${WEIGHTED_METRICS_CONFIG[type]}_2`,
        wRetentionValue3: `${WEIGHTED_METRICS_CONFIG[type]}_3`,
        wRetentionValue7: `${WEIGHTED_METRICS_CONFIG[type]}_7`,
      };
    }
    return {
      wRetentionValue2: `${WEIGHTED_METRICS_CONFIG[type]}_2`,
      wRetentionValue3: `${WEIGHTED_METRICS_CONFIG[type]}_3`,
    };
  }, [type]);

  const weightedRetentionList = ['wd237', 'ww23', 'wm23'];
  const retentionMetricsList = ['d237', 'w23', 'm23', ...weightedRetentionList];

  // 卡片配置
  const tabConfig = useMemo(() => ENGAGEMENT_METRICS_TRENDS_TAB_LIST[type] || ENGAGEMENT_METRICS_TRENDS, [type, getLocale()]);

  // 更新数据
  const updateData = (res: never[]) => {
    let chartOption: any = [];
    let columnData: any = [];
    let lineData: any = [];
    let tableData: any = [];
    if (selectedValue && selectedValue !== 'accumulated') {
      chartOption = res || [];
      lineData = changeDataByDimension(chartOption, CHART_TYPE.LINE);
      columnData = changeDataByDimension(chartOption, CHART_TYPE.COLUMN);
      tableData = changeDataByDimension(chartOption, CHART_TYPE.TABLE);
    } else {
      chartOption = res?.Nodes?.map((item: { [x: string]: any; date: any; }) => ({
        data: item[dataType.replace('.', '_')],
        date: item.date,
      }));
      columnData = changeDataByChartType(chartOption, CHART_TYPE.COLUMN);
      lineData = changeDataByChartType(chartOption, CHART_TYPE.LINE);
      tableData = changeDataByChartType(chartOption, CHART_TYPE.TABLE);
    }
    setData({
      ...data,
      [CHART_TYPE.COLUMN]: columnData,
      [CHART_TYPE.LINE]: lineData,
      [CHART_TYPE.TABLE]: tableData,
    } as any);
  };

  const onChartTypeChange = (chartType: { id: React.SetStateAction<string>; }) => {
    setChartType(chartType.id);
  };

  // data type change
  const onDataTypeChange = (dataType: string, text: React.SetStateAction<string>, selected: any) => {
    update?.(dataType, selected);
    setSelectedValue(selected);
    setDataType(dataType);
    setDataTypeText(text);
  };

  // 时间处理
  const handleDate = (date: moment.MomentInput) => (type === MONTH ? moment(date).format("YYYY-MM") : moment(date).format("YYYY-MM-DD"));

  const processChartData = (data: any[], dataType: string, isPercent: boolean, isUser: boolean | undefined) => data.map(item => {
    if (dataType === 'arpu') return formatSmallValue(item,'decimals',2);
    return isPercent
      ? formatPercentNumNoPercent(parseFloat(item), 2)
      : formatNum(item, false, isUser, '$', false);
  });

  // 根据图表类型改变数据格式
  const changeDataByChartType = (chartOption: any, chartType: string) => {
    const CHART_DATA: any = [];

    if (isDay) {
      const { 
        THIS_DATE, LAST_DATE, LAST_DATE_MONTH, LAST_DATE_YEAR
      } = LINE_METRICS_LEGEND_MAP[DAY];

      CHART_DATA.push(THIS_DATE, LAST_DATE, LAST_DATE_MONTH, LAST_DATE_YEAR);
    } else {
      const { THIS_DATE, LAST_DATE } = LINE_METRICS_LEGEND_MAP[type];

      CHART_DATA.push(THIS_DATE, LAST_DATE);
    }
    
    const isWeighted = weightedRetentionList.includes(dataType);
    const d2 = isWeighted ? wRetention2 : retention2;
    const d3 = isWeighted ? wRetention3 : retention3;
    const d7 = isWeighted ? wRetention7 : retention7;

    const d2Value = isWeighted ? wRetentionValue2 : retentionValue2;
    const d3Value = isWeighted ? wRetentionValue3 : retentionValue3;
    const d7Value = isWeighted ? wRetentionValue7 : retentionValue7;

    const chartMap: any = {
      [CHART_TYPE.COLUMN]: () => {
        // d237 指标需要做特殊处理
        if (retentionMetricsList.includes(dataType)) {
          let legend = isDay ? [d2, d3, d7] : [d2, d3];
          legend = legend.map((item) => item);
          // 横坐标
          const xAxis: string[] = [];
          // 纵坐标
          const yAxis: any = {};
          // 数据配置
          const seriesBase = {
            type: 'bar',
            barMaxWidth: 80,
            cursor: 'pointer',
          };
          const data: {
            [key: string]: string[];
          } = isDay ? {
            [d2]: [],
            [d3]: [],
            [d7 as any]: [],
          } : {
            [d2]: [],
            [d3]: [],
          };
          chartOption?.forEach?.((item: { date: moment.MomentInput; data: { [x: string]: string; }; }) => {
            xAxis.push(handleDate(item?.date));
            data[d2].push(item?.data?.[d2Value]);
            data[d3].push(item?.data?.[d3Value]);
            isDay && data[d7 as any].push(item?.data?.[d7Value]);
          });

          yAxis.axisLabel = {
            formatter (value: number) {
              if (value === 0) {
                return "0%";
              }
              return formatPercentNum(value);
            }
          };

          const d2Data = data[d2]?.map((item) => isPercent ? formatPercentNumNoPercent(parseFloat(item), 2) : formatNum(item, false, isUser, '$', false)) || [];
          const d3Data = data[d3]?.map((item) => isPercent ? formatPercentNumNoPercent(parseFloat(item), 2) : formatNum(item, false, isUser, '$', false)) || [];
          const d7Data = data[d7]?.map((item: string) => isPercent ? formatPercentNumNoPercent(parseFloat(item), 2) : formatNum(item, false, isUser, '$', false)) || [];

          return {
            series: isDay ? [
              {
                ...seriesBase,
                name: legend[0],
                data: d2Data,
              },
              {
                ...seriesBase,
                name: legend[1],
                data: d3Data,
                selected: false,
              },
              {
                ...seriesBase,
                name: legend[2],
                data: d7Data,
              },
            ] : [
              {
                ...seriesBase,
                name: legend[0],
                data: d2Data,
              },
              {
                ...seriesBase,
                name: legend[1],
                data: d3Data,
              },
            ],
            xAxis,
            yAxis,
            legend,
          };
        } 
        const selectedLegend: Record<string, boolean> = {};

        const legend = (() => {
          const name = tabConfig?.find(item => item.value === dataType)?.text || getTabText(dataType);
          return CHART_DATA.map((chartData: any) => {
            const legendEntry = `${name} (${chartData?.name})`;
            if (chartData.selected === false) {
              selectedLegend[legendEntry] = false;
            }
            return legendEntry;
          });
        })();

        const yAxis: any = {};
        // 横坐标
        const xAxis: string[] = [];
        // 数据配置
        const seriesBase = {
          type: 'bar',
          barMaxWidth: 80,
          cursor: 'pointer',
        };

        const data: {
          [key: string]: string[];
        } = CHART_DATA.reduce((accumulator: any, chartData: { key: string | number; }) => {
          accumulator[chartData.key] = [];
          return accumulator;
        }, {});
          
        chartOption?.forEach?.((item: any) => {
          const date = handleDate(item?.date);
          CHART_DATA.forEach((chartData: { key: any; }) => {
            const dataKey = chartData.key;
            const value = item?.data?.[dataKey];
            data[dataKey].push(value);
          });
          xAxis.push(date);
        });
          
          
        const seriesData = CHART_DATA.map((chartData: { key: any; }, index: number) => {
          const dataKey = chartData.key;
          const processedData = processChartData(data[dataKey], dataType, isPercent, isUser);
          
          return {
            ...seriesBase,
            name: legend[index],
            data: processedData,
            z: legend.length - index, // 最为前面的z-value越高
          };
        });
          
        const series = seriesData;
          
        return {
          series,
          yAxis,
          xAxis,
          legend,
          selectedLegend
        };
        
      },
      [CHART_TYPE.LINE]: () => {
        const data = chartMap[CHART_TYPE.COLUMN]();

        return {
          ...data,
          series: data?.series?.map((seriesItem: { data: any[]; }) => ({
            ...seriesItem,
            // data:itemData,
            type: 'line',
          })),
        };
      },
      
      [CHART_TYPE.TABLE]: () => {
        // d237 指标需要做特殊处理
        if (retentionMetricsList.includes(dataType)) {
          const option = [
            {
              key: 'date',
              align: 'left',
              fixed: 'left',
              width: 100,
              header: formatMessage({ id: 'overview_table_date' }),
              render: (cvm: { date: moment.MomentInput; }) => {
                const weekDay = getWeekDay(cvm?.date);
                const isSunOrSat = weekDay === 'Sun' || weekDay === 'Sat' || weekDay === '周日' || weekDay === '周六';
                return <>
                  <span className='table-text'>{handleDate(cvm?.date)}</span>
                  {type?.toLowerCase() !== 'month' && <span className={isSunOrSat ? 'text-week-b' : 'text-week'}>{weekDay}</span>}
                </>;
              },
            },
            {
              key: d2,
              align: 'right',
              fixed: 'left',
              width: 100,
              header: d2,
              render: (cvm: { [x: string]: string; }) => (<span>{formatPercentNum(parseFloat(cvm[d2]))}</span>),
            },
            {
              key: d3,
              align: 'right',
              fixed: 'left',
              width: 100,
              header: d3,
              render: (cvm: { [x: string]: string; }) => (<span>{formatPercentNum(parseFloat(cvm[d3]))}</span>),
            },
          ];
          isDay && option.push({
            key: d7 as any,
            align: 'right',
            fixed: 'left',
            width: 100,
            header: d7 as any,
            render: (cvm: { [x: string]: string; }) => (<span>{formatPercentNum(parseFloat(cvm?.[d7 as any]))}</span>),
          });
          const data = chartOption?.map((item: { date: any; data: { [x: string]: any; }; }) => {
            const newData = ({
              date: item?.date,
              [d2]: item?.data && item.data[d2Value],
              [d3]: item?.data && item.data[d3Value],
            });
            isDay && (newData[d7 as any] = item?.data?.[d7Value]);
            return newData;
          });
          return {
            option,
            data
          };
        } 
        const unit = REVENUE_METRICS_TRENDS?.find(item => item.value === dataType)?.unit;
          
        const legend = (() => {
          const name = tabConfig?.find(
            item => item.value === dataType)?.text || getTabText(dataType);
          return CHART_DATA.map((chartData: { name: any; }) => `${name} (${chartData?.name})`);
        })();
          
        const renderCellValue = (cvm: any, key: any, isPercent: any, isUser: any, unit: any, index?: string | number) => {
          const value = cvm[key];
          const _val = isPercent
            ? formatPercentNum(parseFloat(value))
            : formatNum(value, false, isUser);
          
          return (
            <span>
              {unit && _val !== '-' ? unit + _val : _val}
            </span>
          );
        };
          
        const option = [
          {
            key: 'date',
            align: 'left',
            fixed: 'left',
            width: 110,
            header: formatMessage({ id: 'overview_table_date' }),
            render: (cvm: { date: moment.MomentInput; }) => {
              const weekDay = getWeekDay(cvm?.date);
              const isSunOrSat =
                  weekDay === 'Sun' || weekDay === 'Sat' || weekDay === '周日' || weekDay === '周六';
              return (
                <>
                  <span className="table-text">{handleDate(cvm?.date)}</span>
                  {type?.toLowerCase() !== 'month' && (
                    <span className={isSunOrSat ? 'text-week-b' : 'text-week'}>{weekDay}</span>
                  )}
                </>
              );
            },
          },
          ...CHART_DATA.map((chartData: { key: any; }, index: string | number) => ({
            key: chartData.key,
            align: 'right',
            width: 100,
            header: legend[index],
            render: (cvm: any) =>
              renderCellValue(
                cvm,
                chartData.key,
                isPercent,
                isUser,
                unit,
                index
              ),
          })),
        ];
          
        const data = chartOption?.map((item: { date: any; data: { [x: string]: any; }; }) => {
          const { date, data } = item;
          return {
            date,
            ...(data || {}),
          };
        });
          
        return {
          option,
          data
        };
        
      },
    };

    return chartMap[chartType]?.();
  };

  // 根据维度改变数据格式
  const changeDataByDimension = (chartOption: any, chartType: string) => {
    const legend: any = [...new Set(chartOption?.map((item: { [x: string]: string; }) => getSelectorText(selectedValue, item[`${selectedValue  }_name`]) ?? ''))];
    const xAxis: any = [...new Set(chartOption?.map((item: { date: moment.MomentInput; }) => handleDate(item?.date) ?? ''))];
    const yAxis: any = {};
    const seriesBase = {
      type: chartType === 'line' ? 'line' : 'bar',
      barMaxWidth: 80,
      barCategoryGap: '50%',
      cursor: 'pointer',
    };
    // payer% 的值需要百分比特殊处理
    // if (hasPercentList?.includes(dataType)) {
    //   yAxis.axisLabel = {
    //     formatter: function (value) {
    //       if (value === 0) {
    //         return "0%"
    //       }
    //       return formatPercentNum(value)
    //     }
    //   }
    // }
    const series = legend?.map((item: any) => {
      const data: any = [];
      chartOption?.map((cItem: { [x: string]: string; metrics: { [x: string]: { value: any; }; }; }) => {
        if (item === getSelectorText(selectedValue, cItem[`${selectedValue  }_name`])) {
          data.push(cItem?.metrics[dataType]?.value ?? '');
        }
      });
      if (chartType === 'column') return { ...seriesBase, name: item, data, stack: 1, };
      return { ...seriesBase, name: item, data };
    });

    const chartMap = {
      [CHART_TYPE.COLUMN]: () => ({ legend, xAxis, yAxis, series }),
      [CHART_TYPE.LINE]: () => ({ legend, xAxis, yAxis, series }),
      [CHART_TYPE.TABLE]: () => {
        const unit = REVENUE_METRICS_TRENDS?.find(item => item.value === dataType)?.unit;
        const data: any = [];
        xAxis?.map((item: any, index: string | number) => {
          const obj = { date: item ?? '' };
          series?.map((v: { data: { [x: string]: any; }; name: string | number; }) => {
            const val = v?.data[index];
            obj[v?.name] = val ?? '';
          });
          data.push(obj);
        });

        const metricOption = legend?.map((item: string) => ({
          key: item,
          align: 'right',
          width: METRIC_COLUMN_WIDTH + TABLE_CELL_PADDING_LEFT + TABLE_CELL_PADDING_RIGHT,
          header: getSelectorText(selectedValue, item),
          render: (cvm: { [x: string]: string | number; }) => {
            const _val = isPercent
              ? formatPercentNum(parseFloat(cvm[item]))
              : formatNum(cvm[item], false, isUser);
            return (<span>{unit && _val !== '-' ? unit + _val : _val}</span>);
          },
        })
        );
        const option = [
          {
            key: 'date',
            align: 'left',
            fixed: 'left',
            width: DATE_COLUMN_WIDTH + TABLE_CELL_PADDING_LEFT + TABLE_CELL_PADDING_RIGHT,
            header: formatMessage({ id: 'overview_table_date' }),
            render: (cvm: { date: moment.MomentInput; }) => {
              const weekDay = getWeekDay(cvm?.date);
              const isSunOrSat = weekDay === 'Sun' || weekDay === 'Sat' || weekDay === '周日' || weekDay === '周六';
              return <>
                <span className='table-text'>{handleDate(cvm?.date)}</span>
                {type?.toLowerCase() !== 'month' && <span className={isSunOrSat ? 'text-week-b' : 'text-week'}>{weekDay}</span>}
              </>;
            },
          },
          ...metricOption
        ];
        return {
          option,
          data
        };
      },
    };
    return chartMap[chartType]?.();
  };

  // 获取卡片选中时Tab文案
  const getTabText = (dataType: string) => getIsDollarText(REVENUE_METRICS_TRENDS.concat(tabConfig)?.find(item => item.value === dataType)?.text);

  // 下载文件
  const downloadData = useCallback(() => {
    const filterName = `${excelFilterName  }_${  getTabText(dataType)}`;
    const dataArr = cloneDeep(data?.[CHART_TYPE.TABLE]?.data) || [];

    const donwLoadData = dataArr?.map((item: { [x: string]: any; date?: any; }) => {
      if (selectedValue !== 'accumulated') {
        Object.keys(item)?.map(v => {
          if (v === 'date') {
            item[v] = getIsErrorValue(item[v]) ? '-' : handleDate(item[v]);
          } else {
            const itemsObj = isPercent ? formatExeclNormalPercent(item[v]) : isUser ? formatExeclNum(item[v]) : formatExeclDecimal(item[v]);
            item[v] = itemsObj;
          }
        });
        return item;
      } 
      const extractedKeysAndValues: { [key: string]: string; } = {};
      const formattedValues: any = {};

      for (const key in LINE_METRICS_LEGEND_MAP[type]) {
        if (LINE_METRICS_LEGEND_MAP[type].hasOwnProperty(key)) {
          extractedKeysAndValues[key] = LINE_METRICS_LEGEND_MAP[type][key].key;
          const value = item?.[extractedKeysAndValues[key]];
          formattedValues[extractedKeysAndValues[key]] = isPercent ? formatExeclNormalPercent(value) : isUser ? formatExeclNum(value) : formatExeclDecimal(value);
        }
      }

      return {
        ...item,
        date: item?.date ? handleDate(item?.date) : '-',
        ...formattedValues
      };
      

    }) || [];
    const tableConfig = {
      option: data?.[CHART_TYPE.TABLE]?.option,
      downloadData: donwLoadData
    };
    handleDownload(tableConfig, filterName);
  }, [data]);

  useEffect(() => {
    setDataType(defaultTab);
    setDataTypeText(defaultTabText);
  }, [defaultTab]);

  // 父级控制 loading
  useEffect(() => {
    setLoading(loadingProps);
  }, [loadingProps]);


  // 父级控制 data
  useEffect(() => {
    if (!defaultData) return;
    updateData(defaultData);
  }, [defaultData, getLocale()]);

  useEffect(() => {
    const newDataTypeText = tabList?.find?.((item: { value: string; }) => item.value === dataType)?.text;
    setDataTypeText(newDataTypeText);
  }, [tabList]);

  return {
    data,
    loading,
    dataType,
    selected: selectedValue,
    chartType,
    dataUnit: REVENUE_METRICS_TRENDS?.find(item => item.value === dataType)?.unit,
    userQuota,
    retentionMetricsList,
    getWeekDayStr,
    downloadData,
    onDataTypeChange,
    onChartTypeChange
  };
};

export default useIndex;
